import { DateTime } from 'luxon'

import assertDefined from '@/utils/assertDefined'
import { isNotNull } from '@/utils/helpers'
import logger from '@/utils/logger'

import type { HostRole, WorldLevel } from '@/queries/constants'
import type {
  LiveHelpRequestFragment,
  LiveHelpRequestWithSessionAndPostFragment,
  LiveHelpRequestWithSessionFragment,
  MessageReactionFragment,
  NotificationFragment,
  PostEventFragment,
  PostEventReactionFragment,
  PostFeedbackFragment,
  PostFragment,
  PostReactionFragment,
  PostUpdatesSubscription,
  SeriesFragment,
  SeriesHostFragment,
  SessionFragment,
  SessionHostFragment,
  UserFragment,
  UserOnlineBaseFragment,
  UserOnlineFragment,
  WorldFragment,
  WorldMemberFragment,
} from '@/client/services/hasura/generated/graphql'
import { getBodyForHiddenEntity } from '@/client/services/hidden'
import type { UserEventTypeWithPoints } from '@/client/services/progression'
import type { SerializedRichText } from '@/client/services/richText/types'
import { getActiveAnniversary } from '@/client/services/user'
import type { UserAuthRole } from '@/client/services/userRole/types'
import type {
  ChannelType,
  PostEventType,
  PostFeedbackRating,
  PostPayloadUserPost,
  PostStatus,
  PostType,
} from '@/client/services/worlds'
import type {
  NotificationPayload,
  NotificationType,
} from '@/client/services/worlds/notifications/types'

import type {
  PostDetails,
  PostEventDetails,
  PostFeedbackDetails,
  ReactionDetails as PostReactionDetails,
} from '@/components/Community/Post/types'

import type { FileUpload } from '../FileUpload/types'
import type {
  HostUserDetails,
  LiveHelpRequestBase,
  LiveHelpRequestWithNullableSessionDetails,
  LiveHelpRequestWithSessionAndPost,
  NotificationDetails,
  OnlineUserBase,
  OnlineUserDetails,
  ReactionDetails,
  SeriesBase,
  SessionBase,
  UserDetails,
  WorldBase,
  WorldMemberDetails,
} from '../types'

const DELETED_USER: UserDetails = {
  id: 0,
  displayName: 'Deleted User',
  username: null,
  avatarUrl: '',
  bio: '',
  keyClubDistrictName: null,
  pronouns: null,
  roles: [],
  points: 0,
  timezone: '',
  activeAnniversary: null,
}

// biome-ignore lint/suspicious/noExplicitAny: TODO: Type this
function returnNullOnTransformError<T extends (...args: any[]) => any>(
  entityName: string,
  fn: T
): (...funcArgs: Parameters<T>) => ReturnType<T> | null {
  return (...args: Parameters<T>): ReturnType<T> | null => {
    try {
      const output = fn(...args)
      return output
    } catch (error) {
      logger.error(
        `[ERROR-FRAGMENTS] Unable to transform fragment to details for entity ${entityName}`,
        { err: error }
      )
      return null
    }
  }
}

export function worldMemberDetailsFromFragment(
  worldMember: WorldMemberFragment
): WorldMemberDetails {
  return {
    joinedAt: DateTime.fromISO(`${worldMember?.created_at}`),
    level: worldMember?.level as WorldLevel,
    goal: worldMember.goal ?? null,
    snoozeGoalUntil:
      worldMember.snooze_goal_until != null
        ? DateTime.fromISO(`${worldMember.snooze_goal_until}`)
        : null,
    // TODO: Is it possible to get the type of details here?
    helpIntents: worldMember.details.helpIntents ?? [],
  }
}

export function userDetailsFromFragment(user: UserFragment): UserDetails {
  return {
    id: user.id,
    displayName: user.display_name ?? '',
    avatarUrl: user.avatar?.url ?? null,
    bio: user.bio ?? '',
    username: user.username ?? null,
    pronouns: user.pronouns == null || user.pronouns.length === 0 ? null : user.pronouns,
    roles: user.user_roles.map(userRole => userRole.role as UserAuthRole),
    keyClubDistrictName: user.key_club_club?.key_club_district?.name ?? null,
    points: user.points ?? 0,
    timezone: user.timezone ?? '',
    activeAnniversary: getActiveAnniversary(DateTime.fromISO(`${user.member_since}`)),
    collegeInfo:
      user.filtered_college_info == null
        ? null
        : {
            name: user.filtered_college_info.name ?? '',
            major: user.filtered_college_info.major ?? '',
          },
  }
}

export function userDetailsOrDeletedUserFromFragment(user: UserFragment | null): UserDetails {
  return user == null ? DELETED_USER : userDetailsFromFragment(user)
}

export function onlineUserDetailsFromFragment(user: UserOnlineFragment): OnlineUserDetails {
  return {
    id: user.id ?? 0,
    displayName: user?.display_name ?? '',
    avatarUrl: user.avatar?.url ?? null,
    username: user.username ?? null,
    bio: user.bio ?? '',
    pronouns: user.pronouns == null || user.pronouns.length === 0 ? null : user.pronouns,
    lastOnlineAt: DateTime.fromISO(`${user.last_online_at}`),
    keyClubDistrictName: user?.key_club_district_name ?? null,
    roles: user.user_roles.map(userRole => userRole.role as UserAuthRole),
    activeAnniversary: getActiveAnniversary(DateTime.fromISO(`${user.member_since}`)),
    timezone: user.timezone ?? '',
  }
}

export function onlineUserBaseFromFragment(user: UserOnlineBaseFragment): OnlineUserBase {
  return {
    id: user.id ?? 0,
    lastOnlineAt: DateTime.fromISO(`${user.last_online_at}`),
  }
}

export function liveHelpRequestBaseFromFragment(
  liveHelpRequest: LiveHelpRequestFragment
): LiveHelpRequestBase {
  return {
    id: liveHelpRequest.id,
    postId: liveHelpRequest.post_id ?? 0,
    sessionId: liveHelpRequest.session_id ?? null,
    userId: liveHelpRequest.user_id ?? null,
    canceledAt:
      liveHelpRequest.canceled_at != null ? DateTime.fromISO(liveHelpRequest.canceled_at) : null,
    completedAt:
      liveHelpRequest.completed_at != null ? DateTime.fromISO(liveHelpRequest.completed_at) : null,
    createdAt: DateTime.fromISO(liveHelpRequest.created_at),
  }
}

export const reactionDetailsFromPostReactionFragment = returnNullOnTransformError(
  'Post Reaction',
  (postReaction: PostReactionFragment): ReactionDetails => {
    const { user } = postReaction
    assertDefined(user, `PostReaction#${postReaction.id} has no user`)

    return {
      user: {
        id: postReaction.user?.id ?? 0,
        displayName: postReaction.user?.display_name ?? '',
      },
      reaction: postReaction.reaction,
      createdAt: DateTime.fromISO(`${postReaction.created_at}`),
    }
  }
)

export const reactionDetailsFromMessageReactionFragment = returnNullOnTransformError(
  'Message Reaction',
  (messageReaction: MessageReactionFragment): ReactionDetails => {
    const { user } = messageReaction
    assertDefined(user, `MessageReaction#${messageReaction.id} has no user`)

    return {
      user: {
        id: messageReaction.user?.id ?? 0,
        displayName: messageReaction.user?.display_name ?? '',
      },
      reaction: messageReaction.reaction,
      createdAt: DateTime.fromISO(`${messageReaction.created_at}`),
    }
  }
)
export const reactionDetailsFromPostEventReactionFragment = returnNullOnTransformError(
  'Post Event Reaction',
  (postEventReaction: PostEventReactionFragment): ReactionDetails => {
    const { user } = postEventReaction
    assertDefined(user, `PostReaction#${postEventReaction.id} has no user`)

    return {
      user: {
        id: postEventReaction.user?.id ?? 0,
        displayName: user.display_name ?? '',
      },
      reaction: postEventReaction.reaction,
      createdAt: DateTime.fromISO(`${postEventReaction.created_at}`),
    }
  }
)

export function sessionHostDetailsFromFragment(sessionHost: SessionHostFragment): HostUserDetails {
  return {
    ...userDetailsOrDeletedUserFromFragment(sessionHost.user),
    role: sessionHost.role as HostRole,
  }
}

export function seriesHostDetailsFromFragment(seriesHost: SeriesHostFragment): HostUserDetails {
  return {
    ...userDetailsOrDeletedUserFromFragment(seriesHost.user),
    role: seriesHost.role as HostRole,
  }
}

export function sessionBaseFromFragment(session: SessionFragment): SessionBase {
  return {
    id: session.id,
    createdAt: DateTime.fromISO(`${session.created_at}`),
    title: session.title ?? session.topic.name,
    startDate: DateTime.fromISO(`${session.start_date}`),
    description: session.description,
    userId: session.user_id,
    hosts: session.session_hosts.map(sessionHost => sessionHostDetailsFromFragment(sessionHost)),
    topic: session.topic,
  }
}

export function seriesBaseFromFragment(series: SeriesFragment): SeriesBase {
  return {
    id: series.id,
    createdAt: DateTime.fromISO(`${series.created_at}`),
    title: series.title,
    description: series.description,
    hosts: series.series_hosts.map(seriesHost => seriesHostDetailsFromFragment(seriesHost)),
  }
}

function activeLiveHelpRequestBaseFromFragments(
  liveHelpRequests: LiveHelpRequestFragment[]
): LiveHelpRequestBase | null {
  const latestLiveHelpRequest = liveHelpRequests.length > 0 ? liveHelpRequests[0] : null

  if (latestLiveHelpRequest == null) {
    return null
  }

  if (latestLiveHelpRequest.canceled_at != null || latestLiveHelpRequest.completed_at != null) {
    return null
  }

  return liveHelpRequestBaseFromFragment(latestLiveHelpRequest)
}

export function postFeedbackDetailsFromFragment(
  postFeedback: PostFeedbackFragment
): PostFeedbackDetails {
  return {
    id: postFeedback.id,
    user: userDetailsFromFragment(postFeedback.user),
    reviewer: userDetailsFromFragment(postFeedback.reviewer),
    postId: postFeedback.post_id,
    rating: postFeedback.rating != null ? (postFeedback.rating as PostFeedbackRating) : null,
    note: postFeedback.note != null ? postFeedback.note : null,
  }
}

export function worldBaseFromFragment(world: WorldFragment): WorldBase {
  return {
    id: world.id,
    title: world.title,
    slug: world.slug,
    iconUrl: world.icon_url,
    primaryColor: world.primary_color,
  }
}

export function liveHelpRequestWithNullableSessionDetailsFromFragment(
  liveHelpRequest: LiveHelpRequestWithSessionFragment
): LiveHelpRequestWithNullableSessionDetails {
  const { session } = liveHelpRequest
  const currentUserSessionRegistration = session?.session_registrations[0] ?? null

  return {
    ...liveHelpRequestBaseFromFragment(liveHelpRequest),
    session:
      session != null && session.world != null
        ? {
            id: session.id,
            host: {
              id: session.session_hosts[0].id,
              user: userDetailsFromFragment(session.session_hosts[0].user),
            },
            hasEnded: session.zoom_end_date != null,
            userAttemptedJoining: currentUserSessionRegistration?.attempted_joining === true,
            world: worldBaseFromFragment(session.world),
          }
        : null,
  }
}

export function liveHelpRequestWithSessionAndPostFromFragment(
  liveHelpRequest: LiveHelpRequestWithSessionAndPostFragment
): LiveHelpRequestWithSessionAndPost {
  const { post } = liveHelpRequest
  return {
    ...liveHelpRequestWithNullableSessionDetailsFromFragment(liveHelpRequest),
    post: post == null ? null : postDetailsFromFragment(post),
  }
}

export function notificationDetailsFromFragment(
  notification: NotificationFragment
): NotificationDetails<NotificationType> {
  const {
    user,
    world,
    post,
    related_user: relatedUser,
    secondary_related_user: secondaryRelatedUser,
    session,
    series,
    user_event,
    topic,
    course,
    assessment,
  } = notification

  return {
    id: notification.id,
    payload: notification.payload as NotificationPayload<NotificationType>,
    user: userDetailsFromFragment(user),
    relatedUser: relatedUser != null ? userDetailsFromFragment(relatedUser) : null,
    secondaryRelatedUser:
      secondaryRelatedUser != null ? userDetailsFromFragment(secondaryRelatedUser) : null,
    world: world != null ? worldBaseFromFragment(world) : null,
    post:
      post?.user != null
        ? {
            id: post.id,
            channel:
              post.channel == null
                ? null
                : {
                    id: post.channel.id,
                    title: post.channel.title,
                    type: post.channel.type as ChannelType,
                    worldLevel:
                      post.channel.world_level == null
                        ? null
                        : (post.channel.world_level as WorldLevel),
                  },
            user: userDetailsFromFragment(post.user),
          }
        : null,
    topic:
      topic != null
        ? {
            id: topic.id,
            name: topic.name,
          }
        : null,
    course:
      course != null
        ? {
            id: course.id,
            name: course.name,
          }
        : null,
    userEvent:
      user_event != null
        ? ({
            type: user_event.event_type,
            payload: user_event.event_payload,
            points: user_event.event_points,
          } as UserEventTypeWithPoints)
        : null,
    session: session != null ? sessionBaseFromFragment(session) : null,
    series: series != null ? seriesBaseFromFragment(series) : null,
    assessment: assessment != null ? { id: assessment.id, title: assessment.title } : null,
    clickedAt: notification.clicked_at == null ? null : DateTime.fromISO(notification.clicked_at),
    notifyAt: DateTime.fromISO(notification.notify_at),
  }
}

/** Begin Global Discussion Channel Fragments */

export type PostPartialUpdates = {
  postDeleted: boolean
  body?: SerializedRichText | null
  attachments?: FileUpload[]
  lastEditedAt?: DateTime | null
  reactions?: PostReactionDetails[]
  liveHelpInfo?: {
    postStatus: PostStatus
    currentLiveHelpRequest: LiveHelpRequestBase | null
  } | null
}

export function partialPostDetailsFromPostUpdates(
  postUpdates: PostUpdatesSubscription['post_by_pk'] | undefined
): PostPartialUpdates {
  const {
    body_safe,
    attachment_urls_safe,
    status,
    channel,
    last_edited_at,
    hidden_at,
    post_reactions: postReactions = [],
    live_help_requests: liveHelpRequests = [],
  } = postUpdates ?? {}

  let body = body_safe
  let attachments: FileUpload[] = []
  if (hidden_at != null) {
    body = getBodyForHiddenEntity('post', 'rich-text')
  } else {
    attachments = (attachment_urls_safe ?? []).map(attachmentUrl => ({
      src: attachmentUrl,
    }))
  }

  return {
    postDeleted: postUpdates?.id == null,
    body,
    attachments,
    lastEditedAt: last_edited_at != null ? DateTime.fromISO(last_edited_at) : null,
    reactions: postReactions.map(reactionDetailsFromPostReactionFragment).filter(isNotNull),
    liveHelpInfo:
      channel?.type === 'question'
        ? {
            postStatus: status as PostStatus,
            currentLiveHelpRequest: activeLiveHelpRequestBaseFromFragments(liveHelpRequests),
          }
        : null,
  }
}

export const postDetailsFromFragment = returnNullOnTransformError(
  'Post',
  (post: PostFragment): PostDetails => {
    const {
      user,
      channel,
      body_safe,
      attachment_urls_safe,
      hidden_at,
      post_events_aggregate: postEventsAggregate,
      live_help_requests: liveHelpRequests,
      post_reactions: postReactions,
      post_follows: postFollows,
      world,
      type,
    } = post

    assertDefined(channel, 'Global post must be in a channel')
    assertDefined(user, `User not found for Post#${post.id}`)

    let body = body_safe
    let attachments: FileUpload[] = []
    if (hidden_at != null) {
      body = getBodyForHiddenEntity('post', 'rich-text')
    } else {
      attachments = (attachment_urls_safe ?? []).map(attachmentUrl => ({
        src: attachmentUrl,
      }))
    }

    const isLiveHelpPost = post.channel?.type === 'question' && world != null

    return {
      id: post.id,
      body,
      type: type as PostType,
      isPinned: post.pinned_scope != null,
      liveHelpInfo: isLiveHelpPost
        ? {
            postStatus: post.status as PostStatus,
            currentLiveHelpRequest: activeLiveHelpRequestBaseFromFragments(liveHelpRequests),
            worldDetails: {
              slug: world.slug,
              title: world.title,
              iconUrl: world.icon_url,
              primaryColor: world.primary_color,
            },
            userWorldLevel: post.user_world_member?.level as WorldLevel,
          }
        : null,
      attachments,
      createdAt: DateTime.fromISO(post.created_at),
      followedAt: postFollows.length === 0 ? null : DateTime.fromISO(postFollows[0].created_at),
      lastEditedAt: post.last_edited_at != null ? DateTime.fromISO(post.last_edited_at) : null,
      user: {
        ...userDetailsFromFragment(user),
        isVerified: user.is_verified ?? false,
      },
      countReplies: postEventsAggregate.aggregate?.count ?? 0,
      payload: post.payload as PostPayloadUserPost,
      channel: {
        id: channel.id,
        title: channel.title,
        type: channel.type as Exclude<ChannelType, 'introduction'>,
      },
      reactions: postReactions.map(reactionDetailsFromPostReactionFragment).filter(isNotNull),
    }
  }
)

export const postEventDetailsFromFragment = returnNullOnTransformError(
  'Post Event',
  ({ postEvent }: { postEvent: PostEventFragment }): PostEventDetails => {
    assertDefined(postEvent.user, `User not found for PostEvent#${postEvent.id}`)

    const body =
      postEvent.hidden_at != null
        ? getBodyForHiddenEntity('post-comment', 'rich-text')
        : postEvent.body_safe
    const attachments =
      postEvent.hidden_at != null
        ? []
        : (postEvent.attachment_urls_safe ?? []).map(attachmentUrl => ({
            src: attachmentUrl,
          }))

    const { world } = postEvent.post

    const worldMember =
      world != null
        ? (postEvent.user.world_members.find(member => member?.world_id === world.id) ?? null)
        : null

    return {
      id: postEvent.id,
      body,
      payload: postEvent.payload,
      attachments,
      createdAt: DateTime.fromISO(postEvent.created_at),
      updatedAt: DateTime.fromISO(postEvent.updated_at),
      isCollapsible: postEvent.is_collapsible,
      clientId: postEvent.client_id,
      type: postEvent.type as PostEventType,
      user: userDetailsFromFragment(postEvent.user),
      reactions: postEvent.post_event_reactions
        .map(reactionDetailsFromPostEventReactionFragment)
        .filter(isNotNull),
      worldInfo:
        worldMember != null && world != null
          ? {
              userWorldLevel: worldMember.level as WorldLevel,
              worldDetails: {
                slug: world.slug,
                title: world.title,
                iconUrl: world.icon_url,
                primaryColor: world.primary_color,
              },
            }
          : undefined,
    }
  }
)
